import type { TabDef } from "./types";
import { ordenarPorNome } from "./ordem";

/** Categorias em ordem alfabética — "Outro" (a sobra) fica sempre por último. */
const CATEGORIAS = [
  ...ordenarPorNome(
    [
      "Material Elétrico",
      "EPI",
      "Ferramentas",
      "Instrumentos",
      "Hidráulica",
      "Consumíveis",
      "Escritório",
      "Manutenção",
      "Serviços",
    ],
    (c) => c,
  ),
  "Outro",
];

const UNIDADES = ["UN", "CX", "PAR", "KG", "G", "M", "M²", "M³", "L", "RL", "BR", "PC", "JG", "CJ", "SC", "RS"];

/**
 * Categorias e unidades acima nasceram menores que as do Suprimentos, e o
 * cadastro é o MESMO (uma tabela só). Um item criado lá pode ter categoria
 * "Instrumentos" ou unidade "M²", que aqui não existiam na lista — o select
 * ficaria em branco e salvar apagaria o valor. As duas listas agora batem.
 */

/** Todas as abas do sistema. A ordem define o menu lateral. */
export const TABS: TabDef[] = [
  {
    key: "itens",
    label: "Itens",
    icon: "box",
    resource: "itens",
    subtitle: "Catálogo de materiais e saldo em estoque",
    // Catálogo é cadastro por nome: abre em ordem alfabética da descrição.
    ordenarPor: "descricao",
    fields: [
      { name: "codigo", label: "Código", type: "text", required: true },
      { name: "descricao", label: "Descrição", type: "text", required: true },
      { name: "categoria", label: "Categoria", type: "select", options: CATEGORIAS },
      { name: "unidade", label: "Unidade", type: "select", options: UNIDADES },
      { name: "localizacao", label: "Localização", type: "text" },
      {
        name: "ncm", label: "NCM", type: "text",
        help: "É o NCM que traz os impostos na entrada da nota (sistema de Suprimentos).",
      },
      { name: "saldo", label: "Saldo", type: "number", computed: true },
      { name: "estoque_minimo", label: "Estoque mínimo", type: "number" },
      {
        name: "estoque_maximo", label: "Estoque máximo", type: "number",
        help: "Usado pelo Suprimentos para sugerir a compra: repõe até o máximo, não só até o mínimo.",
      },
      {
        name: "custo_unitario", label: "Custo unitário", type: "currency",
        help: "Custo médio. É recalculado sozinho a cada entrada com preço — só preencha para abrir o saldo inicial.",
      },
      { name: "valor_estoque", label: "Valor em estoque", type: "currency", computed: true },
      { name: "situacao", label: "Situação", type: "status", computed: true },
      { name: "status", label: "Status", type: "status", options: ["Ativo", "Inativo"] },
    ],
  },
  {
    key: "alertas",
    label: "Alertas de Estoque",
    icon: "alert",
    resource: "alertas",
    subtitle: "Itens abaixo do mínimo ou zerados — para reposição/compra",
    custom: "alertas",
    fields: [],
  },
  {
    // RECEBIMENTO DE MATERIAIS — a entrada de material com conferência. Vem
    // antes de Movimentações no menu porque é o começo do fluxo: o material
    // chega, é conferido e SÓ ENTÃO vira movimentação de estoque.
    key: "recebimentos",
    label: "Recebimento de Materiais",
    icon: "swap",
    resource: "recebimentos",
    subtitle: "Entrada de material com conferência — nota, fotos e divergências",
    custom: "recebimentos",
    fields: [],
  },
  {
    key: "movimentacoes",
    label: "Movimentações",
    icon: "swap",
    resource: "movimentacoes",
    subtitle: "Entradas e saídas de material — o mesmo extrato do Suprimentos",
    fields: [
      { name: "item_id", label: "Item", type: "select", ref: "itens", required: true },
      { name: "data", label: "Data", type: "date", required: true },
      { name: "tipo", label: "Tipo", type: "status", required: true, options: ["Entrada", "Saída"] },
      {
        // Lista alinhada com a do Suprimentos: as duas telas escrevem na MESMA
        // tabela, e dois vocabulários para a mesma coluna quebram o filtro e o
        // relatório ("Requisição" aqui, "Requisição interna" lá).
        name: "motivo",
        label: "Motivo",
        type: "select",
        options: [
          "Compra", "Requisição interna", "Consumo em obra", "Devolução de obra",
          "Devolução ao fornecedor", "Transferência", "Ajuste de inventário",
          "Saldo inicial", "Descarte/Perda",
        ],
      },
      { name: "quantidade", label: "Quantidade", type: "number", required: true },
      {
        name: "valor_unitario", label: "Valor unitário", type: "currency",
        help: "Preço da entrada. É ele que recalcula o custo médio do item — deixe em branco em saída e em ajuste.",
      },
      { name: "documento", label: "Documento (NF/Req.)", type: "text" },
      { name: "requisitante", label: "Requisitante / destino", type: "text" },
      { name: "centro_custo", label: "Centro de custo", type: "text" },
      { name: "responsavel", label: "Responsável", type: "text" },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  {
    key: "inventarios",
    label: "Inventários",
    icon: "clipboard",
    resource: "inventarios",
    subtitle: "Contagens, divergência e acurácia de estoque",
    fields: [
      { name: "item_id", label: "Item", type: "select", ref: "itens", required: true },
      { name: "data_contagem", label: "Data da contagem", type: "date", required: true },
      { name: "quantidade_sistema", label: "Qtd. sistema", type: "number", required: true },
      { name: "quantidade_contada", label: "Qtd. contada", type: "number", required: true },
      { name: "divergencia", label: "Divergência", type: "number", computed: true },
      { name: "situacao", label: "Situação", type: "status", computed: true },
      { name: "responsavel", label: "Responsável", type: "text" },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  // Solicitações = caixa de entrada dos pedidos de separação vindos da Gestão da
  // Operação (gravados na tabela `separacoes`). Tela própria (custom) com a ação
  // "Atender" → gera o Termo de retirada (aba Documentos) que baixa o estoque.
  {
    key: "solicitacoes",
    label: "Solicitações",
    icon: "truck",
    resource: "separacoes",
    subtitle: "Pedidos de separação da Gestão da Operação — atender gera o termo de retirada",
    custom: "solicitacoes",
    fields: [],
  },
  // Aba "Fornecedores" removida do menu a pedido (RETIRAR ITEM). A tabela e o
  // recurso de API continuam existindo no banco — basta readicionar a aba aqui
  // para reativá-la, sem perder dados.
  {
    key: "documentos",
    label: "Documentos",
    icon: "documento",
    resource: "documentos",
    subtitle: "Termos de retirada e devolução — foto do item + assinaturas",
    custom: "documentos",
    fields: [],
  },
  // A aba "Usuários" foi REMOVIDA: a criação de logins é centralizada no Portal
  // SERCAMP (lá se define o usuário e quais sistemas ele pode acessar). Criar
  // login aqui gerava conta que o portão de acesso do Portal não reconhecia.
  // O acesso passa a ser sempre pelo card do Portal (SSO). Ver server/auth.ts.
  {
    /* Aba de todos os sistemas: o pedido cai na fila do Desenvolvimento. */
    key: "solicitar_atualizacao",
    label: "Solicitar Atualização",
    icon: "clipboard",
    resource: "solicitar_atualizacao",
    subtitle:
      "Marque uma reunião com o Desenvolvimento, peça um ajuste ou avise um erro. O pedido vai direto para quem cuida dos sistemas, com o seu nome e a data.",
    fields: [],
  },
];
